import { useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useActivateExam } from '../hooks/useActivateExam';
import { paths } from '../routes/paths';
import { examsSelectors } from '../slices/exams/examsSlice';
import { isNclexExam, NCLEX_EXAM_ID, STEP1_EXAM_ID } from '../utils/examUtils';
import type { RootState } from '../store/store';
import { MCGRAW_HILL_HANDBOOK_EXAM_ID } from './mcgrawHill';
import { selectIsHandbookActive } from './nursingCourses';
import { WORKING_BOOK_SLUG } from './mcgrawHillCatalog';
import { selectMcGrawHillBook } from './mcgrawHillService';
import { refreshAfterMcGrawHillChange } from './refreshAfterMcGrawHillChange';
import {
  MCGRAW_HILL_VERTICAL_SCOPES,
  setMcGrawHillVertical,
  type McGrawHillVerticalKey,
} from './mcgrawHillVerticalScope';

/**
 * mcgraw-hill (ORA-1247): the launcher's switch into a vertical. Makes the
 * vertical's first exam active, remembers the vertical, then opens the AI
 * desktop. On a failure the member stays on the launcher with a snackbar (the
 * exam write reports its own) and the vertical is left as it was.
 */
export const useSwitchMcGrawHillVertical = () => {
  const navigate = useNavigate();
  const store = useStore<RootState>();
  const { enqueueSnackbar } = useSnackbar();
  const { activateExam } = useActivateExam();

  const [switchingTo, setSwitchingTo] = useState<McGrawHillVerticalKey | null>(
    null
  );

  // One switch at a time, even before `switchingTo` re-renders.
  const isSwitchingRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const reportFailure = () =>
    enqueueSnackbar('Unable to open this demo. Please try again.', {
      variant: 'error',
      autoHideDuration: 4000,
    });

  /** Katzung under Preclinical, the catalog's own select. */
  const selectKatzung = async (): Promise<boolean> => {
    try {
      await selectMcGrawHillBook(WORKING_BOOK_SLUG);
    } catch {
      reportFailure();
      return false;
    }

    // Selected already; a failed refresh only leaves stale state until the
    // desktop loads its own (as in the catalog).
    await refreshAfterMcGrawHillChange().catch(() => undefined);
    return true;
  };

  /**
   * USMLE Step 1 primary (Medicine), through the exam menu's own write. That
   * write reports its own failure; the store says whether it took.
   */
  const selectStep1 = async (): Promise<boolean> => {
    const isStep1Primary = (current: RootState) =>
      examsSelectors.selectPrimaryExam(current)?.id === STEP1_EXAM_ID;

    const step1 = examsSelectors.selectById(store.getState(), STEP1_EXAM_ID);

    if (!step1) {
      reportFailure();
      return false;
    }

    if (!isStep1Primary(store.getState())) await activateExam(step1);
    return isStep1Primary(store.getState());
  };

  /**
   * NCLEX-RN primary when the account holds it, else the DUT Nursing Handbook
   * as the active custom exam under Preclinical, through the exam menu's own
   * write. That write reports its own failure; the store says whether it took.
   */
  const selectNursing = async (): Promise<boolean> => {
    const state = store.getState();
    const nclexExam = examsSelectors.selectById(state, NCLEX_EXAM_ID);

    const handbook = examsSelectors.selectById(
      state,
      MCGRAW_HILL_HANDBOOK_EXAM_ID
    );

    if (nclexExam) {
      if (!isNclexExam(examsSelectors.selectPrimaryExam(state))) {
        await activateExam(nclexExam);
      }
      return isNclexExam(examsSelectors.selectPrimaryExam(store.getState()));
    }

    if (!handbook) {
      reportFailure();
      return false;
    }

    if (!selectIsHandbookActive(state)) await activateExam(handbook);
    return selectIsHandbookActive(store.getState());
  };

  /**
   * Makes the vertical's first exam active (no navigation). The launcher's
   * switch, and the textbook viewer's Home, which undoes the textbook's exam.
   */
  const selectFirstExam = async (
    vertical: McGrawHillVerticalKey
  ): Promise<boolean> => {
    const { firstExam } = MCGRAW_HILL_VERTICAL_SCOPES[vertical];
    try {
      if (firstExam === 'step1') return await selectStep1();
      if (firstExam === 'katzung') return await selectKatzung();
      if (firstExam === 'nursing') return await selectNursing();
      return true;
    } catch {
      reportFailure();
      return false;
    }
  };

  const switchTo = async (
    vertical: McGrawHillVerticalKey
  ): Promise<boolean> => {
    if (isSwitchingRef.current) return false;

    isSwitchingRef.current = true;
    setSwitchingTo(vertical);
    let isSwitched: boolean;
    try {
      isSwitched = await selectFirstExam(vertical);
    } finally {
      isSwitchingRef.current = false;
    }

    if (!isMountedRef.current) return false;
    if (!isSwitched) {
      setSwitchingTo(null);
      return false;
    }

    setMcGrawHillVertical(vertical);
    navigate(paths.home);
    return true;
  };

  return { switchTo, switchingTo, selectFirstExam };
};
